import { accessibleBrandColors } from '../utils/accessibleColors';
// © 2025 COPYright LAHO – https://www.linkedin.com/in/laurens-holtkamp-7a686b2b/

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useUser } from './AuthContext';
import { supabase } from '../lib/supabase';
import { uniqueGroups } from '../utils/groupMemberships';

const GroupContext = createContext();

const DEFAULT_BRANDING = {
  primaryRgb: '168 85 247',
  primaryLightRgb: '216 180 254',
  primaryDarkRgb: '107 33 168',
};

const getSelectedGroupStorageKey = (userId) =>
  `bb_selected_group_${userId}`;

const readStoredGroupId = (userId) => {
  if (
    typeof window === 'undefined' ||
    !userId
  ) {
    return null;
  }

  return window.localStorage.getItem(
    getSelectedGroupStorageKey(userId)
  );
};

const writeStoredGroupId = (
  userId,
  groupId
) => {
  if (
    typeof window === 'undefined' ||
    !userId
  ) {
    return;
  }

  const storageKey =
    getSelectedGroupStorageKey(userId);

  if (groupId) {
    window.localStorage.setItem(
      storageKey,
      groupId
    );
  } else {
    window.localStorage.removeItem(
      storageKey
    );
  }
};

const applyBranding = (branding) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const safeBranding = accessibleBrandColors((branding || DEFAULT_BRANDING).primaryRgb);

  root.style.setProperty(
    '--bb-primary-rgb',
    safeBranding.primaryRgb ||
      DEFAULT_BRANDING.primaryRgb
  );

  root.style.setProperty(
    '--bb-primary-light-rgb',
    safeBranding.primaryLightRgb ||
      DEFAULT_BRANDING.primaryLightRgb
  );

  root.style.setProperty(
    '--bb-primary-dark-rgb',
    safeBranding.primaryDarkRgb ||
      DEFAULT_BRANDING.primaryDarkRgb
  );
};

export const GroupProvider = ({
  children,
}) => {
  const { user, authLoaded } = useUser();

  const [group, setGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loadingGroup, setLoadingGroup] =
    useState(true);

  const [branding, setBranding] = useState(
    DEFAULT_BRANDING
  );

  const fetchBranding = useCallback(
    async (groupId) => {
      if (!groupId) {
        setBranding(DEFAULT_BRANDING);
        applyBranding(DEFAULT_BRANDING);
        return;
      }

      const { data, error } = await supabase
        .from('group_settings')
        .select('primary_color')
        .eq('group_id', groupId)
        .maybeSingle();

      if (
        error?.code === '42703' ||
        error?.code === '42P01' ||
        error?.code === 'PGRST205'
      ) {
        setBranding(DEFAULT_BRANDING);
        applyBranding(DEFAULT_BRANDING);
        return;
      }

      if (error) {
        console.error(
          '[GroupContext] Fout bij ophalen branding:',
          error
        );

        setBranding(DEFAULT_BRANDING);
        applyBranding(DEFAULT_BRANDING);
        return;
      }

      const primaryColor =
        (data?.primary_color || '').trim();

      if (
        !/^#[0-9a-fA-F]{6}$/.test(
          primaryColor
        )
      ) {
        setBranding(DEFAULT_BRANDING);
        applyBranding(DEFAULT_BRANDING);
        return;
      }

      const hex = primaryColor.slice(1);

      const red = parseInt(
        hex.slice(0, 2),
        16
      );

      const green = parseInt(
        hex.slice(2, 4),
        16
      );

      const blue = parseInt(
        hex.slice(4, 6),
        16
      );

      const lighten = (value, amount) =>
        Math.max(
          0,
          Math.min(
            255,
            Math.round(
              value +
                (255 - value) * amount
            )
          )
        );

      const darken = (value, amount) =>
        Math.max(
          0,
          Math.min(
            255,
            Math.round(
              value * (1 - amount)
            )
          )
        );

      const nextBranding = {
        primaryRgb:
          `${red} ${green} ${blue}`,
        primaryLightRgb:
          `${lighten(red, 0.5)} ` +
          `${lighten(green, 0.5)} ` +
          `${lighten(blue, 0.5)}`,
        primaryDarkRgb:
          `${darken(red, 0.35)} ` +
          `${darken(green, 0.35)} ` +
          `${darken(blue, 0.35)}`,
      };

      setBranding(nextBranding);
      applyBranding(nextBranding);
    },
    []
  );

  const refreshGroup = useCallback(
    async () => {
      if (!authLoaded) {
        return;
      }

      if (!user) {
        setGroup(null);
        setGroups([]);
        await fetchBranding(null);
        setLoadingGroup(false);
        return;
      }

      console.log(
        '[GroupContext] Stap 1: Start refreshGroup()'
      );

      setLoadingGroup(true);

      const {
        data: memberships,
        error: memberErr,
      } = await supabase
        .from('group_users')
        .select(
          'group_id, role, groups (id, name, owner_id)'
        )
        .eq('user_id', user.id);

      if (memberErr) {
        console.error(
          '[GroupContext] Fout bij ophalen van group_users:',
          memberErr
        );

        setGroup(null);
        setGroups([]);
        await fetchBranding(null);
        setLoadingGroup(false);
        return;
      }

      console.log(
        '[GroupContext] Stap 2: Aantal groepen gevonden:',
        memberships?.length || 0
      );

      const nextGroups = uniqueGroups((
        memberships || []
      )
        .map((membership) => {
          const membershipGroup =
            Array.isArray(
              membership.groups
            )
              ? membership.groups[0]
              : membership.groups;

          if (!membershipGroup) {
            return null;
          }

          return {
            id:
              membershipGroup.id ||
              membership.group_id,
            name: membershipGroup.name,
            owner_id:
              membershipGroup.owner_id,
            role: membership.role,
          };
        })
        .filter(Boolean))
        .sort((first, second) =>
          (first.name || '').localeCompare(
            second.name || ''
          )
        );

      setGroups(nextGroups);

      if (nextGroups.length === 0) {
        console.log(
          '[GroupContext] Stap 3: Geen groepen gevonden'
        );

        setGroup(null);

        writeStoredGroupId(
          user.id,
          null
        );

        await fetchBranding(null);
        setLoadingGroup(false);
        return;
      }

      const storedGroupId =
        readStoredGroupId(user.id);

      const nextGroup =
        nextGroups.find(
          (candidate) =>
            candidate.id === storedGroupId
        ) || nextGroups[0];

      console.log(
        '[GroupContext] Stap 4: Groep gevonden:',
        nextGroup.name
      );

      setGroup(nextGroup);

      writeStoredGroupId(
        user.id,
        nextGroup.id
      );

      await fetchBranding(nextGroup.id);
      setLoadingGroup(false);
    },
    [
      authLoaded,
      user,
      fetchBranding,
    ]
  );

  useEffect(() => {
    refreshGroup();
  }, [refreshGroup]);

  const selectGroup = useCallback(
    async (groupId) => {
      const nextGroup =
        groups.find(
          (candidate) =>
            candidate.id === groupId
        ) || null;

      setGroup(nextGroup);

      writeStoredGroupId(
        user?.id,
        nextGroup?.id
      );

      await fetchBranding(nextGroup?.id);
    },
    [
      fetchBranding,
      groups,
      user?.id,
    ]
  );

  const activateGroup = useCallback(
    async (groupId) => {
      if (!user?.id || !groupId) return;
      writeStoredGroupId(user.id, groupId);
      await refreshGroup();
    },
    [refreshGroup, user?.id]
  );

  return (
    <GroupContext.Provider
      value={{
        group,
        groups,
        loadingGroup,
        branding,
        refreshGroup,
        selectGroup,
        activateGroup,
      }}
    >
      {children}
    </GroupContext.Provider>
  );
};

export const useGroup = () =>
  useContext(GroupContext);
